import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useCatalog } from "@/lib/useCatalog";
import { api } from "@/lib/api";
import { money, MODE_META } from "@/lib/pricing";
import { Navbar } from "@/components/Navbar";
import { AvailabilityCalendar } from "@/components/AvailabilityCalendar";
import { CakeCustomizer } from "@/components/CakeCustomizer";
import { Button } from "@/components/ui/button";
import { Zap, CalendarHeart, Star, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

function todayISO(offset = 0) {
  const d = new Date(); d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Order() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const catalog = useCatalog();
  const cart = useCart();
  const [searchParams] = useSearchParams();
  const presetMode = searchParams.get("mode");
  const presetStyle = searchParams.get("style");

  const [step, setStep] = useState("mode"); // mode | date | style | customize
  const [mode, setModeLocal] = useState(cart.mode || null);
  const [date, setDateLocal] = useState(cart.date || null);
  const [blocked, setBlocked] = useState([]);
  const [activeStyle, setActiveStyle] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);

  useEffect(() => {
    if (!(catalog && presetMode && step === "mode")) return;
    if (!["same_day", "next_day", "pre_order"].includes(presetMode)) return;
    if (presetMode === "next_day") {
      if (!orderInfo) return;
      if (!orderInfo.next_day_available) return;
    }
    chooseMode(presetMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, presetMode, orderInfo]);

  useEffect(() => {
    api.get("/availability").then(({ data }) => setBlocked(data.blocked_dates || [])).catch(() => {});
    api.get("/ordering-info").then(({ data }) => setOrderInfo(data)).catch(() => {});
  }, []);

  if (!catalog) {
    return (<div className="App"><Navbar /><div className="grid place-items-center py-32 text-sugar-ink2">Loading the bakery…</div></div>);
  }

  const styles = catalog.styles.filter((s) => s.modes.includes(mode));
  const nextDayOk = orderInfo ? orderInfo.next_day_available : true;

  const chooseMode = (m) => {
    setModeLocal(m);
    if (m === "pre_order") { setStep("date"); }
    else {
      const d = m === "same_day" ? todayISO(0) : todayISO(1);
      setDateLocal(d);
      cart.setMode(m, d);
      setStep("style");
    }
  };

  const chooseDate = (d) => {
    setDateLocal(d);
    cart.setMode("pre_order", d);
    if (presetStyle) {
      const s = catalog.styles.find((x) => x.id === presetStyle && x.modes.includes("pre_order"));
      if (s) { setActiveStyle(s); setStep("customize"); return; }
    }
    setStep("style");
  };

  const chooseStyle = (s) => { setActiveStyle(s); setStep("customize"); };

  const addToCart = (item) => {
    cart.addItem(item);
    toast.success(`${item.style_name} added — ${money(item.unit_price)}`);
    setActiveStyle(null);
    setStep("style");
  };

  const modeCards = [
    { key: "same_day", icon: Zap },
    { key: "next_day", icon: CalendarHeart },
    { key: "pre_order", icon: Star },
  ];

  return (
    <div className="App min-h-screen bg-sugar-bg">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
        {/* stepper */}
        <div className="mb-8 flex items-center gap-2 text-xs font-bold">
          {["Mode", mode === "pre_order" ? "Date" : "When", "Style", "Customize"].map((label, i) => {
            const idx = ["mode", mode === "pre_order" ? "date" : "when", "style", "customize"];
            const activeI = ["mode", "date", "style", "customize"].indexOf(step === "date" ? "date" : step);
            const on = i <= activeI;
            return (
              <div key={label} className="flex items-center gap-2">
                <span className={`grid h-6 w-6 place-items-center rounded-full ${on ? "bg-sugar-pink text-white" : "bg-sugar-blush text-sugar-muted"}`}>{i + 1}</span>
                <span className={on ? "text-sugar-ink" : "text-sugar-muted"}>{label}</span>
                {i < 3 && <span className="text-sugar-muted">/</span>}
              </div>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          {/* STEP MODE */}
          {step === "mode" && (
            <motion.div key="mode" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <h1 className="font-display text-3xl font-extrabold text-sugar-ink lg:text-4xl">When do you need your cake?</h1>
              <p className="mt-2 text-sugar-ink2">Choose how you'd like to order{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</p>
              <div className="mt-8 grid gap-5 md:grid-cols-3">
                {modeCards.map(({ key, icon: Icon }) => {
                  const accent = { same_day: "#C68B98", next_day: "#A9757F", pre_order: "#3B2A23" }[key] || "#3B2A23";
                  const today = orderInfo?.today || todayISO(0);
                  const tomorrow = new Date(new Date(today + "T12:00:00").getTime() + 86400000).toISOString().slice(0, 10);
                  const dayBlocked = (key === "same_day" && blocked.includes(today)) || (key === "next_day" && blocked.includes(tomorrow));
                  const disabled = dayBlocked || (key === "next_day" && !nextDayOk);
                  return (
                  <button
                    key={key}
                    data-testid={`order-mode-${key}`}
                    disabled={disabled}
                    onClick={() => { if (!disabled) chooseMode(key); }}
                    className={`rounded-2xl border-2 bg-white p-7 text-left transition-all duration-200 ${disabled ? "cursor-not-allowed opacity-50" : "hover:-translate-y-1 hover:shadow-xl"}`}
                    style={{ borderColor: accent + "40" }}
                  >
                    <span className="grid h-12 w-12 place-items-center rounded-xl text-white shadow-md" style={{ backgroundColor: accent }}><Icon size={22} strokeWidth={2.4} /></span>
                    <h3 className="mt-4 font-display text-xl font-bold text-sugar-ink">{MODE_META[key].label}</h3>
                    <p className="mt-2 text-sm font-semibold" style={{ color: accent }}>{disabled ? (dayBlocked ? "Sorry, this day is unavailable" : "Order by 6 PM for next-day") : MODE_META[key].blurb}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold" style={{ color: accent }}>{disabled ? "Unavailable now" : <>Choose <ArrowRight size={15} /></>}</span>
                  </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* STEP DATE */}
          {step === "date" && (
            <motion.div key="date" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="grid gap-8 lg:grid-cols-[420px_1fr]">
              <div>
                <button onClick={() => setStep("mode")} className="mb-4 text-sm font-semibold text-sugar-ink2 hover:text-sugar-pink">← Back</button>
                <h1 className="font-display text-3xl font-extrabold text-sugar-ink">Pick your date</h1>
                <p className="mt-2 text-sugar-ink2">Blocked & sold-out dates are greyed out. Pre-orders need at least 3 days' notice.</p>
                <div className="mt-6">
                  <AvailabilityCalendar blocked={blocked} selected={date} onSelect={chooseDate} />
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="overflow-hidden rounded-2xl sugar-card-shadow">
                  <img src="https://images.unsplash.com/photo-1604413191066-4dd20bedf486?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxjdXN0b20lMjBjZWxlYnJhdGlvbiUyMGNha2UlMjByYXNwYmVycnklMjBzdHJhd2JlcnJ5fGVufDB8fHx8MTc4OTM0NjgyM3ww&ixlib=rb-4.1.0&q=85" alt="cake" className="h-full w-full object-cover" />
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP STYLE */}
          {step === "style" && (
            <motion.div key="style" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <button onClick={() => setStep(mode === "pre_order" ? "date" : "mode")} className="mb-4 text-sm font-semibold text-sugar-ink2 hover:text-sugar-pink">← Back</button>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h1 className="font-display text-3xl font-extrabold text-sugar-ink">Choose your style</h1>
                  <p className="mt-1 text-sugar-ink2">
                    {MODE_META[mode].label}{date ? ` · ${date}` : ""}
                    {mode !== "pre_order" && <span className="ml-1 rounded-full bg-sugar-blush px-2 py-0.5 text-xs font-bold text-sugar-pink">Quick menu</span>}
                  </p>
                </div>
                {cart.items.length > 0 && (
                  <Button data-testid="order-goto-checkout" onClick={() => navigate("/checkout")} className="rounded-full bg-sugar-pink font-bold text-white hover:bg-sugar-pinkhover">
                    Cart ({cart.items.length}) · {money(cart.subtotal)} <ArrowRight size={16} className="ml-1.5" />
                  </Button>
                )}
              </div>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {styles.map((s) => {
                  const sizeGroup = s.groups.find((g) => g.id === "size");
                  const from = sizeGroup ? (Number(s.base_price) || 0) + Math.min(...sizeGroup.options.map((o) => o.price)) : s.base_price;
                  return (
                    <button key={s.id} data-testid={`cake-style-${s.id}`} onClick={() => chooseStyle(s)} className="group overflow-hidden rounded-2xl border border-sugar-border bg-white text-left sugar-lift">
                      <div className="relative overflow-hidden">
                        <img src={s.image} alt={s.name} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <span className="absolute right-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-sugar-pink">from {money(from)}</span>
                      </div>
                      <div className="p-5">
                        <h3 className="font-display text-lg font-bold text-sugar-ink">{s.name}</h3>
                        <p className="mt-1 text-sm text-sugar-ink2">{s.tagline}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* STEP CUSTOMIZE */}
          {step === "customize" && activeStyle && (
            <motion.div key="customize" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <CakeCustomizer style={activeStyle} onAdd={addToCart} onBack={() => { setActiveStyle(null); setStep("style"); }} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
